import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, LogOut, ChevronDown } from 'lucide-react';
import { useAuthStore } from '../../stores/auth-store';

// ─────────────────────────────────────────────────────────────
// UserMenu — logged-in user shown top-right in the TopBar.
//   avatar (initials) + username + role → light dropdown
//   (Change password · Sign out). Modeled on the RecentEdits
//   popover: anchored below-right, closes on outside-click / Escape.
// ─────────────────────────────────────────────────────────────

/** Initials for the avatar: role capitals (e.g. "SA") else first 2 of username. */
function userInitials(username?: string, role?: string): string {
  if (role) {
    const caps = role.replace(/[^A-Z]/g, '');
    if (caps.length >= 2) return caps.slice(0, 2);
  }
  return (username ?? '??').slice(0, 2).toUpperCase();
}

export function UserMenu() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const initials = userInitials(user?.username, user?.role);

  const goChangePassword = () => { setOpen(false); navigate('/dashboard/change-password'); };
  const doLogout = () => { setOpen(false); logout(); };

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          height: 36, padding: '0 6px 0 4px',
          background: open ? 'var(--color-bg)' : 'transparent',
          border: '1px solid transparent',
          borderRadius: 8, cursor: 'pointer',
          transition: 'background 120ms',
        }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = 'var(--color-bg)'; }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Avatar — same green gradient + initials as the old sidebar block */}
        <span
          style={{
            width: 28, height: 28, borderRadius: 14,
            background: 'linear-gradient(135deg, #6fbf4a, #487f31)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 11, fontWeight: 600, flexShrink: 0,
          }}
        >
          {initials}
        </span>

        {/* Name / role — hidden on small screens (avatar-only) */}
        <span className="hidden md:flex" style={{ flexDirection: 'column', minWidth: 0, lineHeight: 1.2, textAlign: 'left' }}>
          <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>
            {user?.username ?? '—'}
          </span>
          <span style={{ fontSize: 10.5, color: 'var(--color-text-dim)' }}>{user?.role ?? ''}</span>
        </span>

        <ChevronDown size={14} className="hidden md:block" style={{ color: 'var(--color-text-dim)', flexShrink: 0 }} />
      </button>

      {open && (
        <div
          ref={popRef}
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)', right: 0,
            minWidth: 208,
            background: 'var(--color-panel)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(15,23,32,0.12)',
            zIndex: 60, padding: 6,
          }}
        >
          <LightMenuItem icon={<KeyRound size={14} />} label="Change password" onClick={goChangePassword} />
          <div style={{ height: 1, margin: '6px 8px', background: 'var(--color-border)' }} aria-hidden />
          <LightMenuItem icon={<LogOut size={14} />} label="Sign out" onClick={doLogout} danger />
        </div>
      )}
    </div>
  );
}

function LightMenuItem({ icon, label, onClick, danger }: {
  icon: ReactNode; label: string; onClick: () => void; danger?: boolean;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      role="menuitem"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 10px',
        background: hover ? (danger ? 'rgba(185,28,28,0.08)' : 'var(--color-panel-alt)') : 'transparent',
        color: danger ? 'var(--color-bad)' : (hover ? 'var(--color-text)' : 'var(--color-text-mid)'),
        border: 'none', borderRadius: 6,
        fontSize: 13, fontWeight: 500, fontFamily: 'var(--font-body)',
        cursor: 'pointer', textAlign: 'left',
        transition: 'background 120ms, color 120ms',
      }}
    >
      <span style={{ display: 'inline-flex', width: 18, justifyContent: 'center', opacity: 0.9 }}>{icon}</span>
      {label}
    </button>
  );
}
